import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { useOrderBookDataById } from '@/lib/hooks/useOrderBookServiceById';
import { NonEmptyArray } from '@/lib/types/helpers';
import { EMarketAction, MarketAction } from '@/lib/types/market-action';
import { MarketIntent } from '@/lib/types/market-intent';
import { OrderBookItem } from '@/lib/types/order-book';
import { isEmptyArray, isNonEmptyArray } from '@/lib/utils/arrays';
import { maxBy, minBy } from '@/lib/utils/numbers';

type UseMarketPriceParams = {
  outcomeId: string;
  intent: MarketIntent;
  action: MarketAction;
};

const getMinAsk = (asks: NonEmptyArray<OrderBookItem>) => {
  return minBy(asks, (ask) => ask.price);
};

const getMaxBid = (bids: NonEmptyArray<OrderBookItem>) => {
  return maxBy(bids, (bid) => bid.price);
};

// based-off OrderBook data asks/bids
export const useMarketPrice = ({
  outcomeId,
  intent,
  action,
}: UseMarketPriceParams): Decimal | null => {
  const effectiveOrderBook = useOrderBookDataById(outcomeId);

  return useMemo(() => {
    if (!effectiveOrderBook) {
      return null;
    }
    const { asks, bids } = effectiveOrderBook[intent];
    if (isEmptyArray(asks) && isEmptyArray(bids)) {
      return null;
    }

    let result: Decimal | null = null;

    if (action === EMarketAction.BUY) {
      if (isNonEmptyArray(asks)) {
        result = getMinAsk(asks).price;
      } else if (isNonEmptyArray(bids)) {
        result = Decimal(1);
      }
    } else if (action === EMarketAction.SELL) {
      if (isNonEmptyArray(bids)) {
        result = getMaxBid(bids).price;
      } else {
        result = Decimal(0);
      }
    }
    return result;
  }, [effectiveOrderBook, intent, action]);
};
